import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, Injector, ErrorHandler, DoBootstrap } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { NgSelectModule } from '@ng-select/ng-select';
import { provideNgxMask, NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
// ... other imports ...

// ... other components and services ...

import { NgxCaptchaModule } from 'ngx-captcha';
import { GeronigoVouchersComponent } from './components/geronigo-vouchers/geronigo-vouchers.component';
import { BasketModalComponent } from './components/basket-modal/basket-modal.component';
import { VoucherComponent } from './components/voucher/voucher.component';
import { BasketComponent } from './components/basket/basket.component';
import { PaymentComponent } from './components/payment/payment.component';
import { SafeHtmlPipe } from './pipe/safehtml.pipe';
import { AudioComponent } from './components/audio/audio.component';
import { VideoRecorderComponent } from './components/video-recorder/video-recorder.component';
import { PaymentService } from './services/payment.service';
import { SESSION_STORAGE_PROVIDERS } from './services/session-storage.service';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { GlobalErrorHandler } from './services/global-error-handler.service';

// ... other components and services ...

const components = [
    GeronigoVouchersComponent,
    BasketModalComponent,
    VoucherComponent,
    BasketComponent,
    PaymentComponent,
    SafeHtmlPipe,
    AudioComponent,
    VideoRecorderComponent
];

// ⚠️ FIX: If you are getting an error here, you likely need to install a compatible 
// version of @ngx-translate/http-loader. The syntax itself is correct for NgModules.
/*export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    // This line is correct for most NgModule setups:
    return new TranslateHttpLoader(http, 'https://apistage.geronigo.com/config/lang/', '.json'); 
}*/

@NgModule({
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    exports: [
        SafeHtmlPipe,
    ],
    // ⚠️ IMPORTANT: Remove "HttpClient" from the imports list.
    // It's provided via provideHttpClient() below.
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        NgSelectModule,
        ToastrModule.forRoot({ timeOut: 5000 }),
        NgxMaskDirective, 
        NgxMaskPipe,
        // The TranslateModule configuration is correct for NgModules:
        /*TranslateModule.forRoot({
            // 💡 If you were on ngx-translate v17+, you would add: fallbackLang: 'en'
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),*/
        NgxCaptchaModule, 
        // NOTE: Including components here is unusual for an NgModule's imports array
        // but it is done for your Angular Elements setup.
        ...components 
    ], 
    providers: [
        PaymentService,
        SESSION_STORAGE_PROVIDERS,
        // This is the new, correct way to configure HttpClient with DI-based interceptors
        provideHttpClient(withXhr(), withInterceptorsFromDi()), 
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideNgxMask(),
        provideTranslateService({
      lang: 'en',
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        // The prefix and suffix are passed as an object parameter
        prefix: 'https://apistage.geronigo.com/config/lang/',
        suffix: '.json',
      }),
    }),
    ] 
})
export class AppModule implements DoBootstrap{
    constructor(
        private injector: Injector
    ) { }

    ngDoBootstrap() {
        const elements: any[] = [
            [GeronigoVouchersComponent, 'geronigo-vouchers'],
            [GeronigoVouchersComponent, 'rezbot-vouchers'],
            [GeronigoVouchersComponent, 'vouchers-cart'],
            [BasketModalComponent, 'basket-modal']
        ];

        for (const [component, name] of elements) {
            if (customElements.get(name)) {
                continue;
            }

            const el = createCustomElement(component, { injector: this.injector });

            try {
                customElements.define(name, el);
            } catch (error: any) {
                // If another script instance defined the same element first, safely ignore it.
                if (!(error instanceof DOMException && error.name === 'NotSupportedError')) {
                    throw error;
                }
            }
        }
    }
}